// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// AEM's event-driven alarm state machine, as data.
//
// Source of truth: helios-aem `docs/guides/alarm-state-machine.md`. The delta
// itself is seeded by `helios-data-alarms` Flyway migrations V013-V015, so a
// change upstream is a migration, not a runtime edit — and this table has to be
// updated by hand to match. There is no API to read it from (helios-aem
// ADR-0005 deliberately has no CRUD over the machine definitions).
//
// Why mirror it here at all: the operator cells are deliberately PARTIAL
// upstream. A gap is a real operator error, answered with 409. Rendering a
// button whose cell is undefined therefore guarantees a 409 the operator can do
// nothing about, so the UI derives its action set from the delta instead of
// guessing. That is the whole reason this file exists.
//
// helios-aem #134 (ADR-0039) replaces both machines with one lifecycle:
// ACTIVE_UNACK, ACTIVE_ACK, CLEARED_UNACK, CLEARED_ACK and CLOSED, with ACK and
// CLOSE as the only operator events. Until it is deployed everywhere this file
// keeps the old tables and ALSO knows CLOSED, so one build reads both: the
// retired WO_*/SHELVED_* states stop arriving after AEM remaps them, and the
// retired events are still accepted upstream (mapped to ACK/CLOSE). open_loop
// has a CLOSE cell from the four live states; closed_loop (findings) has none
// yet, pending AEM confirming apply-event CLOSE works on finding ids.
//
// Only the operator columns are transcribed. ALARM_TRIGGERED and ALARM_CLEARED
// are ingest-only (otlp_mapper is their sole producer) and are rejected by the
// request schema before they reach a machine, so they have no place in a UI
// table.

/** `open_loop` — bound to `event_type = 'alarm'`. q0 is `CLEARED_ACK`. */
export type OpenLoopState =
  | 'ACTIVE_UNACK'
  | 'ACTIVE_ACK'
  | 'CLEARED_UNACK'
  | 'CLEARED_ACK'
  | 'SHELVED_ACTIVE'
  | 'SHELVED_CLEARED'
  | 'WO_CREATED'
  | 'WO_COMPLETED'
  | 'CLOSED';

/** `closed_loop` — bound to `event_type = 'finding'`. q0 is `ACTIVE_UNACK`. */
export type ClosedLoopState =
  | 'ACTIVE_UNACK'
  | 'ACTIVE_ACK'
  | 'WO_CREATED'
  | 'WO_COMPLETED'
  | 'CLEARED_ACK'
  | 'CLOSED';

export type AlarmFsmState = OpenLoopState | ClosedLoopState;

/** The operator-invokable subset of the machine's input alphabet (Sigma). */
export type OperatorEvent =
  | 'ACK'
  | 'SHELVE'
  | 'UNSHELVE'
  | 'WO_CREATE'
  | 'WO_COMPLETE'
  | 'WO_REOPEN'
  | 'FORCE_CLEAR'
  | 'CLOSE';

export type AlarmMachine = 'open_loop' | 'closed_loop';

/**
 * Which machine governs a row.
 *
 * Upstream binds on the row's `event_type`, but `AlarmRecord` does not expose
 * that column — so it is inferred from the endpoint the row came from instead.
 * `GET /alarms` applies a fixed, non-overridable `event_type = 'alarm'` and
 * `GET /findings` a fixed `event_type = 'finding'`, which makes the inference
 * sound rather than a guess. Confirm with the alarms team if `event_type` is
 * ever added to the read model.
 */
export const MACHINE_BY_SOURCE = {
  alarm: 'open_loop',
  finding: 'closed_loop',
} as const satisfies Record<string, AlarmMachine>;

export const INITIAL_STATE: Record<AlarmMachine, AlarmFsmState> = {
  // q0. An alarm whose stored status is null, or is not a state its machine's
  // transitions mention, resets here before delta is evaluated.
  open_loop: 'CLEARED_ACK',
  closed_loop: 'ACTIVE_UNACK',
};

type Delta = Record<string, Partial<Record<OperatorEvent, AlarmFsmState>>>;

// Self-loops are stored explicitly, exactly as upstream stores them: there is
// no implicit identity transition. A missing key is an undefined cell -> 409.
const OPEN_LOOP_DELTA: Delta = {
  CLEARED_ACK: { ACK: 'CLEARED_ACK', SHELVE: 'SHELVED_CLEARED', CLOSE: 'CLOSED' },
  CLEARED_UNACK: { ACK: 'CLEARED_ACK', SHELVE: 'SHELVED_CLEARED', CLOSE: 'CLOSED' },
  ACTIVE_UNACK: {
    ACK: 'ACTIVE_ACK',
    SHELVE: 'SHELVED_ACTIVE',
    WO_CREATE: 'WO_CREATED',
    CLOSE: 'CLOSED',
  },
  ACTIVE_ACK: {
    ACK: 'ACTIVE_ACK',
    SHELVE: 'SHELVED_ACTIVE',
    WO_CREATE: 'WO_CREATED',
    CLOSE: 'CLOSED',
  },
  SHELVED_ACTIVE: { ACK: 'SHELVED_ACTIVE', SHELVE: 'SHELVED_ACTIVE', UNSHELVE: 'ACTIVE_ACK' },
  SHELVED_CLEARED: { ACK: 'SHELVED_CLEARED', SHELVE: 'SHELVED_CLEARED', UNSHELVE: 'CLEARED_ACK' },
  WO_CREATED: { ACK: 'WO_CREATED', WO_CREATE: 'WO_CREATED', WO_COMPLETE: 'WO_COMPLETED' },
  WO_COMPLETED: { ACK: 'WO_COMPLETED', WO_COMPLETE: 'WO_COMPLETED', WO_REOPEN: 'WO_CREATED' },
  // helios-aem #134: closed by an operator. Terminal, no outgoing transition.
  // CLOSE is offered from the four single-lifecycle states, including the active
  // ones (agreed Oct 26: an operator may close an active alarm; a new activation
  // then opens a new alarm). The retired WO_*/SHELVED_* states are not given a
  // CLOSE cell: the remap moves those rows onto the four states first.
  CLOSED: {},
};

// FORCE_CLEAR is the only way out of an active closed-loop alarm — ALARM_CLEARED
// is absorbed everywhere it appears. SHELVE/UNSHELVE are not in this machine's
// alphabet at all, so they reject as `unknown_input` even though they are valid
// OperatorEvent members. WO_CREATE requires ACTIVE_ACK: acknowledgement is a
// precondition for opening a work order here.
// CLOSE ("Not an alarm" on a finding) works on finding ids: confirmed in dev on
// Oct 26 (apply-event CLOSE returned 204 and the finding read back as CLOSED).
const CLOSED_LOOP_DELTA: Delta = {
  ACTIVE_UNACK: { ACK: 'ACTIVE_ACK', FORCE_CLEAR: 'CLEARED_ACK', CLOSE: 'CLOSED' },
  ACTIVE_ACK: {
    ACK: 'ACTIVE_ACK',
    WO_CREATE: 'WO_CREATED',
    FORCE_CLEAR: 'CLEARED_ACK',
    CLOSE: 'CLOSED',
  },
  WO_CREATED: { ACK: 'WO_CREATED', WO_CREATE: 'WO_CREATED', WO_COMPLETE: 'WO_COMPLETED' },
  WO_COMPLETED: {
    ACK: 'WO_COMPLETED',
    WO_COMPLETE: 'WO_COMPLETED',
    WO_REOPEN: 'WO_CREATED',
    FORCE_CLEAR: 'CLEARED_ACK',
  },
  // Only CLOSE leaves it (a cleared finding can still be closed as not an alarm).
  CLEARED_ACK: { ACK: 'CLEARED_ACK', CLOSE: 'CLOSED' },
  // helios-aem #134: closed by an operator. Terminal in both lifecycles.
  CLOSED: {},
};

const DELTA: Record<AlarmMachine, Delta> = {
  open_loop: OPEN_LOOP_DELTA,
  closed_loop: CLOSED_LOOP_DELTA,
};

/** True when `state` is a state this machine's transitions mention. */
export function isKnownState(machine: AlarmMachine, state: string | null | undefined): boolean {
  return !!state && state in DELTA[machine];
}

/**
 * Resolve `state`, applying the same reset rule upstream's `step()` does: a null
 * or unrecognized status falls back to q0 before delta is evaluated.
 */
export function resolveState(
  machine: AlarmMachine,
  state: string | null | undefined
): AlarmFsmState {
  return isKnownState(machine, state) ? (state as AlarmFsmState) : INITIAL_STATE[machine];
}

/** `delta(state, event)`, or undefined when the cell is undefined (a 409). */
export function transitionFor(
  machine: AlarmMachine,
  state: string | null | undefined,
  event: OperatorEvent
): AlarmFsmState | undefined {
  return DELTA[machine][resolveState(machine, state)]?.[event];
}

/**
 * Every event with a defined cell — i.e. everything that will NOT 409.
 * Includes absorbing self-loops, so this is the safety check, not the UI list.
 */
export function allowedEvents(
  machine: AlarmMachine,
  state: string | null | undefined
): OperatorEvent[] {
  const row = DELTA[machine][resolveState(machine, state)] ?? {};
  return (Object.keys(row) as OperatorEvent[]).filter(e => row[e] !== undefined);
}

/**
 * The events worth offering an operator: allowed AND actually changing state.
 *
 * Self-loops are dropped because they succeed while doing nothing — an
 * "Acknowledge" button on an already-acknowledged alarm would return 204 and
 * change no state, which reads as a broken button rather than a no-op.
 */
export function effectiveEvents(
  machine: AlarmMachine,
  state: string | null | undefined
): OperatorEvent[] {
  const resolved = resolveState(machine, state);
  return allowedEvents(machine, resolved).filter(
    e => transitionFor(machine, resolved, e) !== resolved
  );
}

// ---------------------------------------------------------------------------
// Presentation-side reads of the state vocabulary.
//
// These are deliberately machine-agnostic: the state NAMES carry the meaning
// (an `_ACK`/`_UNACK` suffix, a `SHELVED_`/`WO_` prefix), and both machines draw
// from the same vocabulary. Only action availability needs to know which machine
// governs a row — see effectiveEvents.
// ---------------------------------------------------------------------------

/**
 * Closed by an operator (helios-aem #134). The only resolved state: the Closed
 * tab and the open/closed split read this. CLEARED_* is NOT closed: the
 * condition went away but nobody has closed the alarm yet, so it stays open
 * (agreed Oct 26).
 */
export function isClosedState(state: string | null | undefined): boolean {
  return state === 'CLOSED';
}

/** The alarm's condition went away (cleared), whether or not it was then closed. */
export function isClearedState(state: string | null | undefined): boolean {
  return state === 'CLEARED_ACK' || state === 'CLEARED_UNACK' || state === 'CLOSED';
}

/**
 * Acknowledged. Only the `_ACK`-suffixed states encode this; SHELVED_* and WO_*
 * do not, because open_loop lets WO_CREATE fire straight from ACTIVE_UNACK, so
 * an open work order does not by itself imply an operator acknowledged anything.
 */
export function isAckedState(state: string | null | undefined): boolean {
  return state === 'ACTIVE_ACK' || state === 'CLEARED_ACK';
}

/** Awaiting acknowledgement. */
export function needsAckState(state: string | null | undefined): boolean {
  return state === 'ACTIVE_UNACK' || state === 'CLEARED_UNACK';
}

/** Shelved — deliberately suppressed by an operator. */
export function isShelvedState(state: string | null | undefined): boolean {
  return state === 'SHELVED_ACTIVE' || state === 'SHELVED_CLEARED';
}

/**
 * A work order exists for this alarm.
 *
 * This replaces sniffing `/wo\b|work order/i` out of the free-text ownerStatus:
 * WO_CREATED/WO_COMPLETED are real, durable states now.
 */
export function hasWorkOrderState(state: string | null | undefined): boolean {
  return state === 'WO_CREATED' || state === 'WO_COMPLETED';
}

const STATE_LABELS: Record<AlarmFsmState, string> = {
  ACTIVE_UNACK: 'Active · unacknowledged',
  ACTIVE_ACK: 'Active · acknowledged',
  CLEARED_UNACK: 'Cleared · unacknowledged',
  CLEARED_ACK: 'Cleared',
  SHELVED_ACTIVE: 'Shelved · active',
  SHELVED_CLEARED: 'Shelved · cleared',
  WO_CREATED: 'Work order open',
  WO_COMPLETED: 'Work order complete',
  CLOSED: 'Closed',
};

/**
 * Human-readable label for a state, or undefined when there is nothing to show.
 *
 * An unrecognized value is echoed back verbatim rather than mapped or hidden: it
 * means a migration added a state this build predates, and showing the raw name
 * is more useful to an operator (and to whoever has to update this table) than
 * silently rendering nothing.
 */
export function stateLabel(state: string | null | undefined): string | undefined {
  if (!state) return undefined;
  return STATE_LABELS[state as AlarmFsmState] ?? state;
}

/**
 * The status an alarm shows an operator. Once a work order exists the alarm's
 * lifecycle continues in the WOMS, so the shown status is the order's own
 * `status_label` (AB#34484): "acknowledged" is not a state beside "In progress".
 * Without an order, the AEM state's label. Every status badge on the alarm
 * detail reads this, so the header and the Next step card cannot disagree.
 */
export function shownStatusLabel(
  state: string | null | undefined,
  workOrder: { status_label?: string | null } | null | undefined
): string | undefined {
  return workOrder?.status_label || stateLabel(state);
}
